import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import {
  BiAggregation,
  ConditionEqualityType,
  FieldConfigurationType,
  Grouping,
  MeasureDateRange,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  AggregationDescriptor,
  AnyDescriptor,
  ConditionField,
  DimensionDescriptor,
  DimensionField,
  MeasureDescriptor,
  ReportField,
} from "../../../../../../../../api/types/svTypes.ts";
import {
  AreaItemType,
  DimensionFieldType,
  ItemDataType,
  MetaItemType,
} from "../../../../../../../../api/types/svTypes.ts";
import { formatToISODate } from "../../../../../utils/dateUtils.ts";
import { toAmountType, toCalculateBy } from "../../../../utilities/measureConverter.ts";
import { MeasureUtils } from "../../utils/MeasureUtils.ts";
import { getDefaultFormatByAggregation } from "../fields/utils/getNewDefaultFormatFormat.ts";
import { createFieldId } from "./createFieldId.ts";
import { isDimensionMeta } from "./metaUtils.ts";

const DEFAULT_DATE_GROUPING = Grouping.ByYear;

function createAggregationField(meta: AggregationDescriptor, areaItemType: AreaItemType): DimensionField {
  const isPersistedAggregation = !!meta.configuration?.persistedAggregationName;

  return {
    type: DimensionFieldType.DIMENSION,
    areaItemType,
    meta: {
      ...meta,
      itemType: MetaItemType.DIMENSION,
      keyFieldName: "",
      isNew: false,
      availableFields: [],
      displayValues: [],
      dictionary: [],
      glAllowed: false,
    },
    config: {
      ...meta.configuration,
      type: FieldConfigurationType.Aggregation,
      guid: createFieldId(meta.name),
      name: meta.configuration?.name ?? meta.name,
      persistedAggregationName: meta.configuration?.persistedAggregationName,
      aggregation: meta.configuration?.aggregation ?? BiAggregation.Sum,
      customLabel: undefined,
      ...(isPersistedAggregation ? { systemLabel: meta.caption } : {}),
      dateRange: meta.configuration?.dateRange ?? MeasureDateRange.EndingBalance,
      useCustomConditions: isPersistedAggregation
        ? false
        : meta.customConditions != null && meta.customConditions.length > 0,
      customConditions: isPersistedAggregation
        ? undefined
        : meta.customConditions?.map((c) => ({
            guid: generateGuid(),
            filter: c.filter!,
          })),
    },
  };
}

export function createConditionField(meta: DimensionDescriptor) {
  const metaClone = structuredClone(meta);
  const values = metaClone.type === ItemDataType.Date ? [formatToISODate(new Date())] : [];
  const field: ConditionField = {
    meta: metaClone,
    config: {
      guid: createFieldId(meta.name),
      filter: { dimensionName: metaClone.name, values, equalityType: ConditionEqualityType.Equal },
      parameter: true,
    },
    areaItemType: AreaItemType.CONDITIONS,
  };
  return field;
}

export function createTabularField(meta: AnyDescriptor): ReportField {
  const copy = structuredClone(meta);
  if (copy.itemType === MetaItemType.DIMENSION) {
    return <DimensionField>{
      type: DimensionFieldType.DIMENSION,
      areaItemType: AreaItemType.FIELDS,
      meta: copy,
      config: {
        type: FieldConfigurationType.Dimension,
        guid: createFieldId(copy.name),
        name: copy.name,
        grouping: copy.type === ItemDataType.Date && copy.options?.asDateReference ? DEFAULT_DATE_GROUPING : undefined,
        customLabel: undefined,
        aggregation: undefined,
      },
    };
  }
  if (copy.itemType === MetaItemType.METRIC) {
    const metricField = MeasureUtils.createMetricField(copy, AreaItemType.FIELDS);
    return metricField;
  }
  if (copy.itemType === MetaItemType.CUSTOM) {
    const customColumnField = MeasureUtils.createCustomColumnField(copy, AreaItemType.FIELDS);
    return customColumnField;
  }
  if (copy.itemType === MetaItemType.AGGREGATION) {
    return createAggregationField(copy, AreaItemType.FIELDS);
  }

  const measureField = MeasureUtils.createMeasureField(copy, AreaItemType.FIELDS);
  measureField.config.calculateByField = toCalculateBy(measureField.meta.units);
  measureField.config.amountType = toAmountType(measureField.meta.units);

  return measureField;
}

function createMeasureFieldWithDateRange(meta: MeasureDescriptor, areaItemType: AreaItemType) {
  const field = MeasureUtils.createMeasureField(meta, areaItemType);
  field.config.dateRange ??= MeasureDateRange.EndingBalance;
  return field;
}

function createMeasureField(meta: MeasureDescriptor) {
  const metaClone = structuredClone(meta);
  return createMeasureFieldWithDateRange(metaClone, AreaItemType.VALUES);
}

export function createRowField(meta: AnyDescriptor) {
  const metaClone = structuredClone(meta);
  if (metaClone.itemType === MetaItemType.MEASURE) {
    return createMeasureFieldWithDateRange(metaClone, AreaItemType.ROWS);
  }
  if (isDimensionMeta(metaClone)) {
    const cloneField: DimensionField = {
      type: DimensionFieldType.DIMENSION,
      meta: metaClone,
      areaItemType: AreaItemType.ROWS,
      config: createDimensionConfig(metaClone),
    };
    return cloneField;
  }
  return undefined;
}

export function createColumnField(meta: AnyDescriptor) {
  const metaClone = structuredClone(meta);
  if (metaClone.itemType === MetaItemType.MEASURE) {
    return createMeasureFieldWithDateRange(metaClone, AreaItemType.COLUMNS);
  }
  if (isDimensionMeta(metaClone)) {
    const columnField: DimensionField = {
      type: DimensionFieldType.DIMENSION,
      meta: metaClone,
      areaItemType: AreaItemType.COLUMNS,
      config: createDimensionConfig(metaClone),
    };
    return columnField;
  }
  return undefined;
}

const createDimensionConfig = (meta: DimensionDescriptor) => {
  const metaClone = structuredClone(meta);
  return {
    type: FieldConfigurationType.Dimension,
    guid: generateGuid(),
    name: metaClone.name,
    displayValues: metaClone.displayValues,
    grouping:
      metaClone.type === ItemDataType.Date && metaClone.options?.asDateReference ? DEFAULT_DATE_GROUPING : undefined,
    customLabel: undefined,
    aggregation: undefined,
  };
};

export function createArgument(meta: DimensionDescriptor) {
  const metaClone = structuredClone(meta);
  const argumentField: DimensionField = {
    type: DimensionFieldType.DIMENSION,
    meta: metaClone,
    areaItemType: AreaItemType.ARGUMENTS,
    config: {
      type: FieldConfigurationType.Dimension,
      guid: generateGuid(),
      name: metaClone.name,
      grouping:
        metaClone.type === ItemDataType.Date && metaClone.options?.asDateReference ? DEFAULT_DATE_GROUPING : undefined,
      customLabel: undefined,
    },
  };
  return argumentField;
}

export function createValueField(meta: AnyDescriptor): ReportField {
  const metaClone = structuredClone(meta);
  if (metaClone.itemType === MetaItemType.DIMENSION) {
    const field: DimensionField = {
      type: DimensionFieldType.DIMENSION,
      areaItemType: AreaItemType.VALUES,
      meta: metaClone,
      config: {
        type: FieldConfigurationType.Aggregation,
        guid: createFieldId(metaClone.name),
        name: metaClone.name,
        customLabel: undefined,
        aggregation: BiAggregation.Count,
        dateRange: MeasureDateRange.EndingBalance,
      },
    };
    field.config.format = getDefaultFormatByAggregation(field, field.config.aggregation);

    return field;
  }

  if (metaClone.itemType === MetaItemType.METRIC) {
    const metricField = MeasureUtils.createMetricField(metaClone, AreaItemType.VALUES);
    return metricField;
  }

  if (metaClone.itemType === MetaItemType.CUSTOM) {
    const customColumnField = MeasureUtils.createCustomColumnField(metaClone, AreaItemType.VALUES);
    return customColumnField;
  }

  if (metaClone.itemType === MetaItemType.AGGREGATION) {
    return createAggregationField(metaClone, AreaItemType.VALUES);
  }

  return createMeasureField(metaClone);
}

export function createTileField(meta: AnyDescriptor): ReportField {
  const copy = structuredClone(meta);
  if (copy.itemType === MetaItemType.DIMENSION) {
    return <DimensionField>{
      type: DimensionFieldType.DIMENSION,
      areaItemType: AreaItemType.FIELDS,
      meta: copy,
      config: {
        type: FieldConfigurationType.Aggregation,
        guid: createFieldId(copy.name),
        name: copy.name,
        customLabel: undefined,
        aggregation: BiAggregation.Count,
        dateRange: MeasureDateRange.EndingBalance,
      },
    };
  }
  if (copy.itemType === MetaItemType.METRIC) {
    const metricField = MeasureUtils.createMetricField(copy, AreaItemType.FIELDS);
    return metricField;
  }

  if (copy.itemType === MetaItemType.CUSTOM) {
    const customColumnField = MeasureUtils.createCustomColumnField(copy, AreaItemType.FIELDS);
    return customColumnField;
  }
  if (copy.itemType === MetaItemType.AGGREGATION) {
    return createAggregationField(copy, AreaItemType.FIELDS);
  }

  const measureField = MeasureUtils.createMeasureField(copy, AreaItemType.FIELDS);
  measureField.config.calculateByField = toCalculateBy(measureField.meta.units);
  measureField.config.amountType = toAmountType(measureField.meta.units);

  return measureField;
}
